<div v-if="is_integration_view" class="integration-view mt-1">
  <div class="container-fluid px-4 py-3">

    <!-- ===============================
         PAGE HEADER
         =============================== -->
    <div class="mb-4">
      <h3 class="fw-bold text-xs mb-1">{{ page_title }}</h3>
      <p class="text-muted text-xs mb-0">{{ page_subtitle }}</p>
    </div>

    <!-- ===============================
         INTEGRATION STATUS CARDS
         =============================== -->
    <div class="row g-3 mb-4">
      <div
        v-for="integration in integrations"
        :key="integration.id"
        class="col-md-6 col-lg-3"
      >
        <div class="card border-primary h-100  shadow-sm rounded-4">
          <div class="card-body p-3">

            <div class="d-flex align-items-start gap-2 mb-3">
              <div :class="['p-2 rounded-3', integration.icon_bg_class]">
                <i :class="['bi', integration.icon, integration.icon_color_class]"></i>
              </div>

              <div class="ms-auto">
                <span :class="['badge text-xs', integration.status_class]">
                  <i :class="['bi', integration.status_icon, 'me-1']"></i>
                  {{ integration.status_text }}
                </span>
              </div>
            </div>

            <div class="fw-semibold text-xs mb-1">
              {{ integration.title }}
            </div>

            <p class="text-muted text-xs mb-2">
              {{ integration.description }}
            </p>

            <p class="text-muted text-xs mb-0">
              <i class="bi bi-clock me-1"></i>
              Last sync: {{ integration.last_sync }}
            </p>

          </div>
        </div>
      </div>
    </div>

    <!-- ===============================
         PILL TABS
         =============================== -->
    <div class="mb-4">
      <div class="d-flex gap-2 flex-wrap">

        <button
          type="button"
          class="btn btn-sm rounded-3 text-xs fw-semibold"
          :class="active_tab === 'verify'
            ? 'btn-primary'
            : 'btn-outline-secondary'"
          @click="active_tab = 'verify'"
        >
          <i class="bi bi-search me-1"></i>
          Verify
        </button>

        <button
          type="button"
          class="btn btn-sm rounded-3 text-xs fw-semibold"
          :class="active_tab === 'history'
            ? 'btn-primary'
            : 'btn-outline-secondary'"
          @click="active_tab = 'history'"
        >
          <i class="bi bi-clock-history me-1"></i>
          Verification History
        </button>

      </div>
    </div>

    <!-- ===============================
         TAB CONTENT
         =============================== -->
    <div>

      <!-- ===============================
           VERIFY TAB
           =============================== -->
      <div v-show="active_tab === 'verify'">
        <div class="row g-3 mb-4">

          <div
            v-for="form in verification_forms"
            :key="form.id"
            class="col-md-4"
          >
            <div class="card border-primary  shadow-sm rounded-4 h-100">
              <div class="card-body p-3">

                <div class="d-flex align-items-center gap-2 mb-3">
                  <i :class="['bi', form.icon, form.icon_class]"></i>
                  <div class="fw-semibold text-xs">
                    {{ form.title }}
                  </div>
                </div>

                <p class="text-muted text-xs mb-3">
                  {{ form.description }}
                </p>

                <div class="mb-3">
                  <label class="form-label fw-semibold text-xs">
                    {{ form.field_label }}
                  </label>
                  <input
                    type="text"
                    class="form-control form-control-sm text-xs"
                    :placeholder="form.field_placeholder"
                    v-model="$data[form.model]"
                  >
                </div>

                <button class="btn btn-primary btn-sm rounded-3 text-xs w-100">
                  <i class="bi bi-search me-1"></i>
                  {{ form.button_text }}
                </button>

              </div>
            </div>
          </div>

        </div>

        <!-- ===============================
             INTEGRATION NOTICE
             =============================== -->
        <div class="alert alert-warning  shadow-sm rounded-4">
          <div class="d-flex gap-2">
            <i class="bi bi-exclamation-triangle"></i>
            <div>
              <div class="fw-semibold text-xs mb-1">
                {{ notice_title }}
              </div>
              <p class="mb-0 text-xs">
                {{ notice_text }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- ===============================
           HISTORY TAB
           =============================== -->
      <div v-show="active_tab === 'history'">
        <div class="card border-primary  shadow-sm rounded-4">

          <div class="card-header border-bottom p-3">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <div class="fw-semibold text-xs mb-1">
                  {{ history_title }}
                </div>
                <p class="text-muted text-xs mb-0">
                  {{ history_subtitle }}
                </p>
              </div>

              <button class="btn btn-outline-secondary btn-sm rounded-3 text-xs">
                <i class="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </button>
            </div>
          </div>

          <div class="card-body">
            <div class="table-responsive">
              <table
                ref="verificationHistoryTable"
                class="table table-hover table-sm mb-0 w-100 global_data_table"
                data-page-length="5"
                data-order-column="5"
                data-order-direction="desc"
                data-non-orderable-targets="[4]"
                data-search-placeholder="Search verification history"
              >
                <thead class="table-light">
                  <tr>
                    <th
                      v-for="col in history_columns"
                      :key="col.key"
                      class="fw-semibold text-xs"
                    >
                      {{ col.label }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="record in verification_history"
                    :key="record.id"
                  >
                    <td class="text-xs" data-label="Type">{{ record.type }}</td>
                    <td class="text-xs" data-label="Reference">
                      <code>{{ record.reference }}</code>
                    </td>
                    <td class="text-xs" data-label="Name">{{ record.name }}</td>
                    <td class="text-muted text-xs" data-label="Source">{{ record.source }}</td>
                    <td data-label="Status">
                      <span :class="['badge text-xs', record.status_class]">
                        <i :class="['bi', record.status_icon, 'me-1']"></i>
                        {{ record.status }}
                      </span>
                    </td>
                    <td class="text-muted text-xs" data-label="Verified At">
                      {{ record.verified_at }}
                    </td>
                  </tr>
                </tbody>

              </table>
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</div>
